/**
 * Evals API contract — metrics, datasets, answer-quality runs, and the
 * zero-leakage compliance gate. Backed by `/api/evals/*`.
 */

import { api } from './api';

export interface EvalMetric {
  name: string;
  display_name: string;
  group: string;
  description: string;
  requires_golden: boolean;
}

export interface EvalDataset {
  id: string;
  name: string;
  description: string;
  row_count: number;
  has_golden: boolean;
}

export interface EvalRunRow {
  question: string;
  answer: string;
  returned_count: number;
  scores: Record<string, number | null>;
}

export interface EvalRunResult {
  summary: Record<string, number | null>;
  rows: EvalRunRow[];
  count: number;
  metrics: string[];
  persona_key: string | null;
}

export interface LeakageFinding {
  query: string;
  leaked: { title: string | null; sensitivity: string[] }[];
}

export interface LeakageResult {
  passed: boolean;
  queries_checked: number;
  restricted_candidates_blocked: number;
  forbidden_sensitivities: string[];
  findings: LeakageFinding[];
  persona_key: string | null;
}

export async function fetchMetrics(): Promise<EvalMetric[]> {
  const res = await api.get<{ metrics: EvalMetric[] }>('/evals/metrics');
  return res.metrics;
}

export async function fetchDatasets(): Promise<EvalDataset[]> {
  const res = await api.get<{ datasets: EvalDataset[] }>('/evals/datasets');
  return res.datasets;
}

export async function runEval(input: {
  dataset_id: string;
  metrics: string[];
}): Promise<EvalRunResult> {
  return api.post<EvalRunResult>('/evals/runs', input);
}

export async function runLeakageCheck(): Promise<LeakageResult> {
  return api.post<LeakageResult>('/evals/leakage', {});
}
